(() => {
  if (window.__ES_CART_HEADER_BADGE__) return;
  window.__ES_CART_HEADER_BADGE__ = true;

  const state = {
    fetchPromise: null,
    fetchTs: 0,
    cartValue: null,
    syncTimer: 0,
    lastCount: null,
    lastOptimisticTs: 0
  };

  const VERIFY_MIN_GAP = 8000;

  function cartUrl(path) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '/') + String(path || '').replace(/^\//, '');
  }

  function getCount(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      const n = cart && typeof cart.item_count !== 'undefined' ? parseInt(cart.item_count, 10) : 0;
      return Number.isFinite(n) && n > 0 ? n : 0;
    }
    const groups = new Map();
    let count = 0;
    cart.items.forEach(item => {
      const props = item && item.properties || {};
      const legacy = String(props._eslb || '').split('|')[0].trim();
      const configurationId = String(props._eyeshells_config_id || legacy || '').trim();
      const quantity = Math.max(0, parseInt(item && item.quantity || 0, 10) || 0);
      if (!configurationId) {
        count += quantity;
        return;
      }
      if (!groups.has(configurationId)) groups.set(configurationId, []);
      if (quantity > 0) groups.get(configurationId).push(quantity);
    });
    // A configured pair can contain frame, lens and Prism component lines. Count
    // the physical configuration quantity once, conservatively using the smallest
    // live component quantity if an interrupted update left a mismatch.
    groups.forEach(quantities => {
      if (quantities.length) count += Math.min.apply(Math, quantities);
    });
    return count;
  }

  function getCartAnchors() {
    const selectors = [
      '.js-cart-link',
      '#header a.cart[href*="/cart"]',
      '.menu_moblie .menuright a.cart[href*="/cart"]',
      '.menu_moblie .menuright a[href*="/cart"]'
    ];
    return Array.from(document.querySelectorAll(selectors.join(','))).filter((el, idx, arr) => el && arr.indexOf(el) === idx);
  }

  function ensureCartDot(anchor) {
    if (!anchor || !anchor.querySelector) return null;
    let dot = anchor.querySelector('.es-cart-dot');
    if (!dot) {
      dot = document.createElement('span');
      dot.className = 'es-cart-dot';
      dot.setAttribute('aria-hidden', 'true');
      anchor.appendChild(dot);
    }
    try {
      anchor.classList.add('js-cart-link', 'es-header-badge-anchor', 'cart');
      anchor.style.overflow = 'visible';
      if (getComputedStyle(anchor).position === 'static') anchor.style.position = 'relative';
    } catch (e) {}
    return dot;
  }

  function setDotVisible(dot, hasItems) {
    if (!dot) return;
    try {
      dot.classList.toggle('is-visible', hasItems);
      dot.style.setProperty('display', 'block', 'important');
      dot.style.setProperty('visibility', hasItems ? 'visible' : 'hidden', 'important');
      dot.style.setProperty('opacity', hasItems ? '1' : '0', 'important');
      dot.style.setProperty('transform', hasItems ? 'scale(1)' : 'scale(.85)', 'important');
      dot.style.setProperty('pointer-events', 'none', 'important');
    } catch (e) {}
  }

  function writeState(count) {
    const value = Math.max(0, parseInt(count, 10) || 0);
    const hasItems = value > 0;
    state.lastCount = value;

    try {
      document.documentElement.classList.toggle('es-cart-has-items', hasItems);
      document.documentElement.setAttribute('data-es-cart-count', String(value));
      if (document.body) document.body.classList.toggle('es-cart-has-items', hasItems);
      if (window.localStorage) {
        localStorage.setItem('es_cart_has_items', hasItems ? '1' : '0');
        localStorage.setItem('es_cart_count', String(value));
      }
    } catch (e) {}

    getCartAnchors().forEach(anchor => {
      try {
        anchor.classList.toggle('es-has-items', hasItems);
        anchor.setAttribute('data-cart-count', String(value));
        anchor.setAttribute('data-es-cart-has-items', hasItems ? '1' : '0');
        anchor.setAttribute('aria-label', hasItems ? `Cart, ${value} item${value === 1 ? '' : 's'}` : 'Cart');
        setDotVisible(ensureCartDot(anchor), hasItems);
      } catch (e) {}
    });
  }

  function optimisticMarkHasItems(minCount = 1) {
    state.lastOptimisticTs = Date.now();
    const optimistic = Math.max(parseInt(minCount, 10) || 1, state.lastCount || 0, 1);
    writeState(optimistic);
  }

  
  function fetchCart(forceFresh = false) {
    const now = Date.now();
    if (!forceFresh && state.cartValue && state.fetchTs && now - state.fetchTs < VERIFY_MIN_GAP) {
      return Promise.resolve(state.cartValue);
    }
    if (state.fetchPromise) return state.fetchPromise;
    if (!window.fetch) return Promise.reject(new Error('fetch unavailable'));
    state.fetchPromise = fetch(cartUrl('cart.js'), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }).then(cart => {
      state.cartValue = cart;
      state.fetchTs = Date.now();
      return cart;
    }).finally(() => {
      state.fetchPromise = null;
    });
    return state.fetchPromise;
  }

  function queueSync(delay = 1200, forceFresh = false) {
    window.clearTimeout(state.syncTimer);
    state.syncTimer = window.setTimeout(() => {
      const now = Date.now();
      if (!forceFresh && state.fetchTs && now - state.fetchTs < VERIFY_MIN_GAP) return;
      fetchCart(forceFresh).then(cart => writeState(getCount(cart))).catch(() => {});
    }, Math.max(0, parseInt(delay, 10) || 0));
  }

  window.EyeshellsHeaderCartBadge = {
    setCount(count) {
      const value = Math.max(0, parseInt(count, 10) || 0);
      state.cartValue = { item_count: value };
      state.fetchTs = Date.now();
      writeState(value);
    },
    clear() {
      this.setCount(0);
    },
    sync(forceFresh = true) {
      return fetchCart(!!forceFresh).then(cart => {
        writeState(getCount(cart));
        return cart;
      }).catch(() => null);
    }
  };

  document.addEventListener('es:cart-empty', () => {
    window.EyeshellsHeaderCartBadge.clear();
  });

  document.addEventListener('es:cart-count:changed', event => {
    const detail = event && event.detail ? event.detail : {};
    if (typeof detail.count !== 'undefined') window.EyeshellsHeaderCartBadge.setCount(detail.count);
  });

  function syncFromLocalCacheOrMarkup() {
    let usedCache = false;
    try {
      if (window.localStorage) {
        const raw = localStorage.getItem('es_cart_count');
        if (raw != null) {
          writeState(parseInt(raw, 10) || 0);
          usedCache = true;
        } else if (localStorage.getItem('es_cart_has_items') === '1') {
          writeState(1);
          usedCache = true;
        }
      }
    } catch (e) {}

    if (!usedCache) {
      const serverHas = document.documentElement.classList.contains('es-cart-has-items');
      const activeBadge = document.querySelector('.es-cart-dot.is-visible');
      writeState(serverHas || activeBadge ? 1 : 0);
    }
  }

  function bindFallbackSubmitSync() {
    const ACTUAL_ADD_BUTTON_SELECTOR = '[name="add"], .single_add_to_cart_button, .btn-addtocart, [data-add-to-cart], .js_addtocart, .engoj-btn-addtocart, .enj-add-to-cart-btn, .shop-button, .es-lb__next';

    function triggerLooksLikeFinalAdd(trigger) {
      const text = String(trigger && (trigger.getAttribute('aria-label') || trigger.textContent || trigger.value || '') || '').trim();
      return /add\s*to\s*cart|update\s*cart|adding/i.test(text) || (trigger && trigger.closest && trigger.closest('.es-lb__window') && trigger.classList.contains('es-lb__next'));
    }

    function isActualAddTrigger(el) {
      if (!el || !el.closest) return false;
      const trigger = el.closest(ACTUAL_ADD_BUTTON_SELECTOR);
      if (!trigger) return false;
      if (trigger.matches('[disabled], .disabled, .shop-button-soldout')) return false;
      if (trigger.hasAttribute('data-es-lensbuilder-launch') || trigger.classList.contains('es-lb__launch')) return false;
      if (trigger.closest('[data-es-lb-root], .es-lb__window, .es-lb__overlay, .quickview-product, .product-quickview')) {
        return triggerLooksLikeFinalAdd(trigger);
      }
      const form = trigger.form || trigger.closest('form');
      if (!form) return triggerLooksLikeFinalAdd(trigger);
      const action = form.getAttribute('action') || '';
      return /\/cart\/add(?:$|[/?#])/i.test(action) || /\/cart\/add\.js(?:$|[?#])/i.test(action);
    }

    document.addEventListener('submit', event => {
      const form = event.target;
      if (!form || !form.getAttribute) return;
      const action = form.getAttribute('action') || '';
      if (/\/cart\/add(?:$|[/?#])/i.test(action) || /\/cart\/add\.js(?:$|[?#])/i.test(action)) {
        optimisticMarkHasItems(1);
        queueSync(2200, false);
      }
    }, true);

    document.addEventListener('click', event => {
      const trigger = event.target && event.target.closest ? event.target.closest(ACTUAL_ADD_BUTTON_SELECTOR) : null;
      if (!trigger || !isActualAddTrigger(trigger)) return;
      optimisticMarkHasItems(1);
      queueSync(2200, false);
    }, true);
  }

  document.addEventListener('es:lensbuilder:direct-cart', () => {
    optimisticMarkHasItems(1);
    // Lens builder navigates to cart after submit. Do not poll cart.js repeatedly here.
  });

  document.addEventListener('es:cart-has-items', () => {
    optimisticMarkHasItems(1);
  });

  document.addEventListener('cart:updated', event => {
    const cart = event && event.detail && event.detail.cart ? event.detail.cart : null;
    if (cart) {
      state.cartValue = cart;
      state.fetchTs = Date.now();
      writeState(getCount(cart));
    } else {
      queueSync(1000, false);
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') queueSync(1200, false);
  });
  window.addEventListener('pageshow', () => queueSync(1200, false));
  window.addEventListener('focus', () => queueSync(1800, false));

  bindFallbackSubmitSync();
  syncFromLocalCacheOrMarkup();
  queueSync(160, true);
})();
